import {
  useResponsiveMobile,
  WbCardContent,
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
  WbNoData,
} from '@agilelab/plugin-wb-platform';
import { FC } from 'react';
import { DescendantsTree } from './DescendantsTree';
import { Box, makeStyles, Paper, Typography } from '@material-ui/core';
import { PoliciesTable } from './PoliciesTable';
import { usePolicyTab } from './PoliciesTabContext';
import clsx from 'clsx';
import { useFlagAndScoreContext } from '../../FlagAndScoreContext';

const useStyles = makeStyles(theme => ({
  policiesContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  baseBox: {
    flex: 1,
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.text.primary,
  },
  error: {
    backgroundColor: theme.palette.bkg.error,
    border: `1px solid ${theme.palette.error.main}`,
  },
  warning: {
    backgroundColor: theme.palette.bkg.warning,
    border: `1px solid ${theme.palette.warning.main}`,
  },
  info: {
    backgroundColor: theme.palette.bkg.info,
    border: `1px solid ${theme.palette.info.main}`,
  },
  summaryLabel: {
    fontWeight: 'bold',
    color: theme.palette.black,
  },
  rightContent: {
    overflowY: 'auto',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  descendantsMobileContainer: {
    maxHeight: 300,
  },
}));

type PoliciesTabProps = {};

export const PoliciesTab: FC<PoliciesTabProps> = ({}) => {
  const { policyViolationsCountMap, resourceInfo, descendants } =
    useFlagAndScoreContext();
  const { policyViolationsState, activeEntityExternalId } = usePolicyTab();
  const classes = useStyles();

  const aggregatedForResource =
    policyViolationsCountMap?.aggregatedViolations?.get(
      resourceInfo.externalId,
    );
  const errorCountAggregate = aggregatedForResource?.get('error') ?? 0;
  const warningCountAggregate =
    (aggregatedForResource?.get('warning') ?? 0) +
    (aggregatedForResource?.get('not_blocking_error') ?? 0);

  const infoCountAggregate = aggregatedForResource?.get('info') ?? 0;

  const getIndividualViolationLabel = (
    severity: 'error' | 'warning' | 'info' | 'not_blocking_error',
    externalId: string,
  ) => {
    const individualMap =
      policyViolationsCountMap?.individualViolations?.get(externalId);

    if (!individualMap) return 0;

    if (severity === 'warning') {
      const warningCount = individualMap.get('warning') ?? 0;
      const notBlockingCount = individualMap.get('not_blocking_error') ?? 0;
      return warningCount + notBlockingCount;
    }

    return individualMap.get(severity) ?? 0;
  };

  const isMobile = useResponsiveMobile();

  const descendantsTree = <DescendantsTree />;

  const detailContent = (
    <WbCardContent className={classes.rightContent}>
      {policyViolationsCountMap && (
        <Box className={classes.policiesContainer}>
          {[
            {
              label: 'Errors',
              count: errorCountAggregate,
              severity: 'error' as const,
            },
            {
              label: 'Warning',
              count: warningCountAggregate,
              severity: 'warning' as const,
            },
            {
              label: 'Info',
              count: infoCountAggregate,
              severity: 'info' as const,
            },
          ].map(({ label, count, severity }) => (
            <Box
              key={severity}
              className={clsx(classes.baseBox, classes[severity])}
            >
              <Typography variant="overline" className={classes.summaryLabel}>
                {label}
              </Typography>
              {activeEntityExternalId ? (
                <Typography variant="h3">
                  {getIndividualViolationLabel(
                    severity,
                    activeEntityExternalId,
                  )}
                  <span style={{ fontSize: 12 }}> of {count}</span>
                </Typography>
              ) : (
                <Typography variant="h3" style={{ fontWeight: 400 }}>
                  {count}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      )}
      {activeEntityExternalId ? (
        <PoliciesTable
          policyViolationsState={policyViolationsState}
          // we put a min height to the table when in mobile
          tableStyle={isMobile ? { flex: '1 1 auto' } : {}}
        />
      ) : (
        <Paper style={{ flex: 1, height: '100%' }}>
          <WbNoData
            header="No items selected"
            text="Select an item in the sidebar to view the number of errors and violations."
          />
        </Paper>
      )}
    </WbCardContent>
  );

  return (
    <>
      {!isMobile && (
        <Paper style={{ height: '100%', overflow: 'hidden', flex: '1 1 0' }}>
          {descendants.size > 0 ? (
            <WbDockableGroup direction="horizontal">
              <WbDockablePanel
                defaultSize={35}
                maxSize={50}
                minSize={20}
                title="Structure"
              >
                {descendantsTree}
              </WbDockablePanel>
              <WbDockableResizeHandle direction="horizontal" />

              <WbDockablePanel defaultSize={65}>
                {detailContent}
              </WbDockablePanel>
            </WbDockableGroup>
          ) : (
            detailContent
          )}
        </Paper>
      )}
      {isMobile && (
        <>
          {descendants.size > 0 && (
            <Box className={classes.descendantsMobileContainer}>
              {descendantsTree}
            </Box>
          )}
          {detailContent}
        </>
      )}
    </>
  );
};

export default PoliciesTab;
